import React, { useEffect, useMemo, useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, ChevronDown } from 'lucide-react';
import { cn } from '../../utils/cn';
import { DirectoryContact } from '../../data/mockData';
interface LookupFieldProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: DirectoryContact[];
  placeholder?: string;
  required?: boolean;
}
interface MenuRect {
  top: number;
  left: number;
  width: number;
}
export function LookupField({
  label,
  value,
  onChange,
  options,
  placeholder = 'Search contacts…',
  required
}: LookupFieldProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [menuRect, setMenuRect] = useState<MenuRect | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const displayValue = open ? query : value;
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => o.name.toLowerCase().includes(q) || o.subtitle.toLowerCase().includes(q));
  }, [query, options]);
  // Position the portal menu against the field, in viewport coordinates.
  const updatePosition = () => {
    if (!fieldRef.current) return;
    const r = fieldRef.current.getBoundingClientRect();
    setMenuRect({
      top: r.bottom + 4,
      left: r.left,
      width: r.width
    });
  };
  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const handle = () => updatePosition();
    window.addEventListener('scroll', handle, true);
    window.addEventListener('resize', handle);
    return () => {
      window.removeEventListener('scroll', handle, true);
      window.removeEventListener('resize', handle);
    };
  }, [open]);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current && !containerRef.current.contains(target) && menuRef.current && !menuRef.current.contains(target)) {
        setOpen(false);
        setQuery('');
      } else if (containerRef.current && !containerRef.current.contains(target) && !menuRef.current) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const commit = (contact: DirectoryContact) => {
    onChange(contact.name);
    setOpen(false);
    setQuery('');
  };
  const clear = () => {
    onChange('');
    setQuery('');
    setOpen(false);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[activeIndex]) commit(results[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };
  const menu = open && menuRect ? createPortal(<div ref={menuRef} style={{
    position: 'fixed',
    top: menuRect.top,
    left: menuRect.left,
    width: menuRect.width,
    zIndex: 50
  }} data-id="element-316">
            <ul role="listbox" className="max-h-64 w-full overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-1 shadow-lg" data-id="element-317">
              {results.length === 0 ? <li className="px-3 py-3 text-sm text-slate-500 dark:text-slate-400" data-id="element-318">
                  No matching contacts found.
                </li> : results.map((contact, i) => <li key={contact.id} role="option" aria-selected={i === activeIndex} onMouseDown={e => {
        e.preventDefault();
        commit(contact);
      }} onMouseEnter={() => setActiveIndex(i)} className={cn('flex cursor-pointer flex-col px-3 py-2', i === activeIndex ? 'bg-indigo-50 dark:bg-indigo-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50')} data-id="element-319">
                    <span className="text-sm font-medium text-slate-900 dark:text-slate-100" data-id="element-320">
                      {contact.name}
                    </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400" data-id="element-321">
                      {contact.subtitle}
                    </span>
                  </li>)}
            </ul>
          </div>, document.body) : null;
  return <div className="relative w-full" ref={containerRef} data-id="element-322">
      {label && <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300" data-id="element-323">
          {label}
          {required && <span className="ml-1 text-red-500 dark:text-red-400" data-id="element-324">*</span>}
        </label>}
      <div className="relative" ref={fieldRef} data-id="element-325">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" data-id="element-326" />
        <input type="text" role="combobox" aria-expanded={open} aria-autocomplete="list" value={displayValue} placeholder={value ? value : placeholder} onChange={e => {
        setQuery(e.target.value);
        setActiveIndex(0);
        if (!open) setOpen(true);
      }} onFocus={() => setOpen(true)} onKeyDown={handleKeyDown} className={cn('flex h-8 w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 pl-8 pr-8 py-1 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-indigo-500', required && !value && !open && 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20')} data-id="element-327" />
        {value && !open ? <button type="button" onClick={clear} aria-label="Clear selection" className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-300" data-id="element-328">
            <X className="h-4 w-4" data-id="element-329" />
          </button> : <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" data-id="element-330" />}
      </div>
      {menu}
    </div>;
}